import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider, CssBaseline, useMediaQuery } from "@mui/material";
import { makeTheme } from "./theme";

const ThemeModeContext = createContext(null);
const STORAGE_KEY = "etf_theme_mode"; // "light" | "dark" | "system"

export const ThemeModeProvider = ({ children }) => {
  const systemPrefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  const [pref, setPref] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || "system";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, pref);
  }, [pref]);

  const mode = pref === "system" ? (systemPrefersDark ? "dark" : "light") : pref;
  const theme = useMemo(() => makeTheme(mode), [mode]);

  const api = useMemo(
    () => ({
      pref, // "system" | "light" | "dark"
      mode, // resolved "light" | "dark"
      setPref,
      toggle: () => setPref((p) => (p === "dark" ? "light" : "dark")),
      setLight: () => setPref("light"),
      setDark: () => setPref("dark"),
      setSystem: () => setPref("system"),
    }),
    [pref, mode]
  );

  return (
    <ThemeModeContext.Provider value={api}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode must be used inside ThemeModeProvider");
  return ctx;
};
